import type { Blueprint } from 'js/src/client';
import { Toast } from 'js/src/components/common/Toast';
import type { PastVideoToMatch } from 'js/src/components/dashboard/utils/dashboardSketchStorage';
import { saveCreateViewSeed } from 'js/src/components/dashboard/utils/dashboardSketchStorage';
import { DASHBOARD_ROUTES } from 'js/src/data/dashboard/dashboard-routes';
import { saveBlueprintMatchToCookie } from 'js/src/data/sketch-controls/sketch-controls-cookies';
import Navigation from 'js/src/libs/navigation';

function getBlueprintReferenceVideoId(blueprint: Blueprint): number | null {
    return (
        blueprint.supported_templates_data?.[blueprint.default_template_id]?.video_id ??
        blueprint.video_ids?.[0] ??
        null
    );
}

/** What a new video's design can be copied from: a blueprint, or a past video to match. */
export type CreateSimilarReference =
    | { kind: 'blueprint'; id: number }
    | ({ kind: 'video' } & PastVideoToMatch);

/**
 * Sends the user to the Create page with the blueprint or video already picked to match, and
 * `text` waiting in the input. A blueprint is saved as the chat setting it is everywhere else; a
 * past video rides the staged prompt, so it is picked for this visit only, as a recipe would be.
 *
 * Staged rather than sent: the Create page is where the user picks a recipe, attaches files and
 * says what the new video is about before submitting, so jumping straight into the chat would
 * skip the step they came here to make.
 *
 * Returns false when the staging could not be saved; the caller surfaces that.
 */
export function stageCreateSimilar(
    reference: CreateSimilarReference,
    { text = '', projectId = null }: { text?: string; projectId?: number | null } = {},
): boolean {
    if (reference.kind === 'blueprint') {
        saveBlueprintMatchToCookie(reference.id);
        return saveCreateViewSeed({ text, projectId });
    }
    const { kind: _, ...referenceVideo } = reference;
    return saveCreateViewSeed({ text, projectId, referenceVideo });
}

/**
 * Toolbox replacement for the legacy blueprint selection flow: rather than starting a video from
 * the blueprint, the user lands on the Create page with it picked to match.
 *
 * Returns true when the selection was handled (staged and navigating, or an error was surfaced);
 * false when the blueprint has no example video to imitate and the caller should fall back to the
 * legacy flow.
 *
 * Only for the standalone blueprints page. The video creation flow's own
 * template picker keeps the legacy flow: navigating to the Create page from
 * there would abandon the session the user already started.
 */
export function createSimilarVideoFromBlueprint(
    blueprint: Blueprint,
    projectId: number | null,
): boolean {
    if (getBlueprintReferenceVideoId(blueprint) === null) {
        return false;
    }

    const saved = stageCreateSimilar({ kind: 'blueprint', id: blueprint.id }, { projectId });
    if (!saved) {
        Toast.error('An error occurred while creating the video.');
        return true;
    }

    analytics.track(
        'Create Similar Video Clicked',
        { destination: 'create_page', source: 'blueprint' },
        () => {
            Navigation.goToLocation(DASHBOARD_ROUTES.CREATE);
        },
    );
    return true;
}
